import type { PropsWithChildren } from "react";
import { createContext, useMemo, useState } from "react";
import type { DocumentCitation } from "./helpers";

interface ContextValue {
  documentCitations: DocumentCitation[];
  activeCitationKey?: string;
  selectCitation: (key: string) => void;
  setRelatedCitations: (citations: DocumentCitation[]) => void;
  setHasUnsavedEdits?: (hasUnsavedEdits: boolean) => void;
}

const ObjectCardContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  fieldCitations?: DocumentCitation[];
  onUnsavedEditsChange?: (hasUnsavedEdits: boolean) => void;
}

export const ObjectCardContextProvider = ({
  fieldCitations,
  onUnsavedEditsChange,
  children,
}: PropsWithChildren<Props>) => {
  const [activeCitationKey, setActiveCitationKey] = useState<string>();
  const [relatedCitations, setRelatedCitations] = useState<DocumentCitation[]>([]);

  const documentCitations = useMemo<DocumentCitation[]>(
    () => [...(fieldCitations ?? []), ...relatedCitations],
    [fieldCitations, relatedCitations]
  );

  const value = useMemo<ContextValue>(
    () => ({
      documentCitations,
      activeCitationKey,
      selectCitation: setActiveCitationKey,
      setRelatedCitations,
      setHasUnsavedEdits: onUnsavedEditsChange,
    }),
    [documentCitations, activeCitationKey, onUnsavedEditsChange]
  );

  return <ObjectCardContext value={value}>{children}</ObjectCardContext>;
};

export default ObjectCardContext;
